export const STATUS_LABELS = {
  created: "Pending",
  confirmed: "Processing",
  packed: "Ready to Ship",
  shipped: "Shipped",
  delivered: "Delivered",
  cancelled: "Cancelled",
};

export const STATUS_STYLES = {
  created: "border-amber-200 bg-amber-50 text-amber-800",
  confirmed: "border-blue-200 bg-blue-50 text-blue-800",
  packed: "border-indigo-200 bg-indigo-50 text-indigo-800",
  shipped: "border-violet-200 bg-violet-50 text-violet-800",
  delivered: "border-emerald-200 bg-emerald-50 text-emerald-800",
  cancelled: "border-rose-200 bg-rose-50 text-rose-800",
};

export const METRICS = [
  { key: "total_sales", label: "Total Sales", currency: true },
  { key: "net_sales", label: "Net Sales", currency: true },
  { key: "total_orders", label: "Total Orders" },
  { key: "pending_orders", label: "Pending Orders" },
  { key: "delivered_orders", label: "Delivered Orders" },
  { key: "total_customers", label: "Total Customers" },
  { key: "aov", label: "AOV", currency: true },
  { key: "gst_collected", label: "GST Collected", currency: true },
];

export const CHART_METRICS = {
  revenue: { label: "Revenue", color: "#10B981", currency: true },
  orders: { label: "Orders", color: "#6366F1", currency: false },
  net_sales: { label: "Net Sales", color: "#34D399", currency: true },
  gst: { label: "GST", color: "#F59E0B", currency: true },
  refunds: { label: "Refunds", color: "#F43F5E", currency: true },
};

const pad = (value) => String(value).padStart(2, "0");

export const dateKey = (value) => (
  `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`
);

const copyDate = (value) => new Date(value.getFullYear(), value.getMonth(), value.getDate());

const subtractDays = (value, days) => {
  const result = copyDate(value);
  result.setDate(result.getDate() - days);
  return result;
};

export const presetRange = (preset) => {
  const today = copyDate(new Date());
  if (preset === "today") {
    return { start: today, end: today };
  }
  if (preset === "yesterday") {
    const yesterday = subtractDays(today, 1);
    return { start: yesterday, end: yesterday };
  }
  if (preset === "7days") {
    return { start: subtractDays(today, 6), end: today };
  }
  if (preset === "30days") {
    return { start: subtractDays(today, 29), end: today };
  }
  if (preset === "this_month") {
    return { start: new Date(today.getFullYear(), today.getMonth(), 1), end: today };
  }
  return {
    start: new Date(today.getFullYear(), today.getMonth() - 1, 1),
    end: new Date(today.getFullYear(), today.getMonth(), 0),
  };
};

export const periodLabel = (range) => {
  const start = new Date(`${range.start_date}T12:00:00`);
  const end = new Date(`${range.end_date}T12:00:00`);
  const formatter = new Intl.DateTimeFormat("en-IN", { day: "numeric", month: "short" });
  return `${formatter.format(start)} — ${formatter.format(end)}`;
};

const chartLabel = (value, mode) => {
  const date = new Date(`${value}T12:00:00`);
  if (mode === "monthly") {
    return new Intl.DateTimeFormat("en-IN", { month: "short", year: "2-digit" }).format(date);
  }
  return new Intl.DateTimeFormat("en-IN", { day: "numeric", month: "short" }).format(date);
};

export const groupChartData = (rows, mode) => {
  if (mode === "daily") {
    return rows.map((row) => ({ ...row, label: chartLabel(row.date, mode) }));
  }
  const grouped = new Map();
  rows.forEach((row) => {
    const date = new Date(`${row.date}T12:00:00`);
    const key = mode === "monthly"
      ? `${date.getFullYear()}-${pad(date.getMonth() + 1)}`
      : dateKey(subtractDays(date, (date.getDay() + 6) % 7));
    const existing = grouped.get(key) || {
      date: key,
      label: chartLabel(key, mode),
      revenue: 0,
      orders: 0,
      net_sales: 0,
      gst: 0,
      refunds: 0,
    };
    ["revenue", "orders", "net_sales", "gst", "refunds"].forEach((field) => {
      existing[field] += Number(row[field] || 0);
    });
    grouped.set(key, existing);
  });
  return [...grouped.values()];
};

export const comparisonText = (current, previous, available, currency, inr) => {
  if (!available || previous === undefined || previous === null || Number(previous) === 0) {
    return "No comparison data";
  }
  const change = ((Number(current || 0) - Number(previous)) / Math.abs(Number(previous))) * 100;
  const direction = change >= 0 ? "+" : "";
  const previousLabel = currency ? inr(previous) : Number(previous).toLocaleString("en-IN");
  return `${direction}${change.toFixed(1)}% vs ${previousLabel}`;
};